import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { queryKeys } from "@/lib/queryKeys";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MiroAnnotations } from "@/components/annotations/MiroAnnotations";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ArrowLeft, Building, Calendar, Clock, CheckCircle2, X, Pause } from "lucide-react";
import { ChantierIcon } from "@/components/chantiers/ChantierIcon";
import { useNavigate, useSearchParams, useLocation, useParams } from "react-router-dom";
import { SuspensionInfo } from "@/types/calendar";
import { useChantierMetrics } from "@/hooks/useChantierMetrics";
import { useClosureStore } from "@/stores/closureStore";
import ProfitabilityView from "@/components/project/ProfitabilityView";
import QuoteManager from "@/components/project/QuoteManager";
import AffectationsList from "@/components/project/AffectationsList";
import InvoiceManager from "@/components/project/InvoiceManager";
import ExpensesManager from "@/components/project/ExpensesManager";
import DelayPenaltySimulator from "@/components/project/DelayPenaltySimulator";
import PaiementClient from "@/components/project/PaiementClient";
import { useCalculations } from "@/hooks/useCalculations";
import ChantiersSidebarPanel from "@/components/gestion-financiere/ChantiersSidebarPanel";
import ChantierSelectorOverlay from "@/components/gestion-financiere/ChantierSelectorOverlay";
import { parseISO, format } from "date-fns";
import { createSnapshotFromMetrics } from "@/services/snapshotService";
import { calculerJoursOuvres } from "@/lib/heures-utils";
import { useChantierDateFin } from "@/hooks/useChantierDateFin";
import { cn } from "@/lib/utils";
import { TabBadge, TAB_TRIGGER_CLASSES } from "@/components/ui/TabBadge";

// Formate la durée estimée de suspension pour l'affichage
function formatDureeEstimee(duree: string | null): string {
  switch (duree) {
    case '1_semaine': return '1 sem.';
    case '2_semaines': return '2 sem.';
    case '1_mois': return '1 mois';
    case 'indeterminee': return 'durée indéterminée';
    case 'personnalisee': return ''; // Affichera la date à la place
    default: return '';
  }
}

// Formate la raison de suspension pour l'affichage
function formatRaison(raison: string | null): string {
  switch (raison) {
    case 'intemperies': return 'Intempéries';
    case 'attente_materiaux': return 'Attente matériaux';
    case 'probleme_client': return 'Problème client';
    case 'autre': return 'Autre raison';
    default: return '';
  }
}

interface Chantier {
  id: string;
  nom_chantier: string;
  client: string;
  statut: string;
  etat_chantier: string;
  categorie?: string;
  rentabilite?: number;
  date_debut_prevue?: string;
  date_fin_estimee?: string;
  pictogramme?: string;
  couleur?: string;
}

// Mapping des paramètres URL vers les valeurs de tabs
const TAB_MAPPING: Record<string, string> = {
  'retard': 'penalty-simulator',
  'penalty-simulator': 'penalty-simulator',
  'profitability': 'profitability',
  'rentabilite': 'profitability',  // Alias français pour liens depuis alertes
  'quote': 'quote',
  'team': 'team',
  'invoices': 'invoices',
  'factures': 'invoices',  // Alias français pour liens depuis alertes
  'expenses': 'expenses',
  'paiement': 'paiement',
};

const FinancialManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { chantierId: chantierIdFromRoute } = useParams<{ chantierId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [selectedChantierId, setSelectedChantierId] = useState<string>("");
  const [showChantierSelector, setShowChantierSelector] = useState(false);
  const [entrepriseId, setEntrepriseId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [devis, setDevis] = useState<any[]>([]);
  const [factures, setFactures] = useState<any[]>([]);
  const [membres, setMembres] = useState<any[]>([]);
  const [frais, setFrais] = useState<any[]>([]);
  const [validatedDelay, setValidatedDelay] = useState<number>(0);
  const [criticalDelayDays, setCriticalDelayDays] = useState<number | null>(null); // Nombre de jours de retard avant déficit
  const [delayPeriod, setDelayPeriod] = useState<{ dateDebut: string | null; dateFin: string | null }>({ dateDebut: null, dateFin: null });
  const [suspensionInfo, setSuspensionInfo] = useState<SuspensionInfo | null>(null);

  // Store de clôture - pour afficher la bannière de retour
  const { chantierId: closureChantierId, fromClosureModal, clearClosure } = useClosureStore();

  // Récupérer l'onglet actif depuis l'URL ou utiliser "profitability" par défaut
  const tabParam = searchParams.get('tab');
  const activeTab = TAB_MAPPING[tabParam || ''] || 'profitability';

  // Fonction pour changer d'onglet et mettre à jour l'URL
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  const { metrics, loading: metricsLoading, refresh: refreshMetrics } = useChantierMetrics(selectedChantierId || '');

  // Pourcentage encaissé pour le badge de l'onglet Paiement
  const { entrepriseId: subEntrepriseId } = useSubscription();
  const { data: paiementTotals } = useQuery({
    queryKey: queryKeys.finances.paiementTotals(subEntrepriseId!, selectedChantierId!),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_paiement_totals', { p_chantier_id: selectedChantierId! });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !!subEntrepriseId && !!selectedChantierId,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (user) {
      loadChantiers();
    }
  }, [user]);

  useEffect(() => {
    if (selectedChantierId) {
      loadChantierData();
    }
  }, [selectedChantierId]);

  // Écouter l'événement custom pour les mises à jour de métriques (depuis AffectationsList, DelaySimulator, etc.)
  useEffect(() => {
    const handleMetricsUpdate = async (e: CustomEvent<{ chantierId: string }>) => {
      // Créer/mettre à jour le snapshot du jour pour le graphique d'évolution
      if (e.detail.chantierId) {
        await createSnapshotFromMetrics(e.detail.chantierId);
      }

      // TOUJOURS recharger la liste des chantiers (pour la sidebar) - peu importe le chantier modifié
      // Ceci permet de mettre à jour la rentabilité de TOUS les chantiers après une rotation
      // silentRefresh = true pour ne pas afficher de loader et préserver l'état de l'UI
      await loadChantiers(true);

      // Recharger les données du chantier sélectionné si c'est lui qui est modifié
      if (e.detail.chantierId === selectedChantierId) {
        await loadChantierData();
      }
    };

    window.addEventListener('chantier-metrics-updated', handleMetricsUpdate as EventListener);
    return () => window.removeEventListener('chantier-metrics-updated', handleMetricsUpdate as EventListener);
  }, [selectedChantierId]);

  // Debounce Realtime — quand l'onglet reprend le focus, Supabase rejoue les events
  // accumulés → sans debounce, loadChantierData() tirait 6 fois → re-renders en rafale
  // → les modaux ouverts (ex: ExpensesManager) peuvent se fermer ou perdre la saisie
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedRealtimeRefresh = useCallback(() => {
    if (realtimeDebounceRef.current) {
      clearTimeout(realtimeDebounceRef.current);
    }
    realtimeDebounceRef.current = setTimeout(() => {
      if (import.meta.env.DEV) console.log('[FinancialManagement] Realtime refresh (debouncé)');
      loadChantierData();
      loadChantiers(true);
    }, 500);
  }, [selectedChantierId]);

  // Cleanup du timeout au démontage
  useEffect(() => {
    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
    };
  }, []);

  // Subscription Realtime pour le chantier sélectionné
  useEffect(() => {
    if (!selectedChantierId) return;

    const channel = supabase
      .channel(`financial_management:${selectedChantierId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chantier_metrics_realtime',
          filter: `chantier_id=eq.${selectedChantierId}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'devis',
          filter: `chantier_id=eq.${selectedChantierId}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affectations_chantiers',
          filter: `chantier_id=eq.${selectedChantierId}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'factures_fournisseurs',
          filter: `chantier_id=eq.${selectedChantierId}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'frais_chantier',
          filter: `chantier_id=eq.${selectedChantierId}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chantiers',
          filter: `id=eq.${selectedChantierId}`,
        },
        debouncedRealtimeRefresh
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedChantierId, debouncedRealtimeRefresh]);

  // silentRefresh = true → ne pas mettre loading = true (pour les refresh en arrière-plan)
  const loadChantiers = async (silentRefresh = false) => {
    try {
      // Ne pas afficher le loader si c'est un refresh silencieux
      if (!silentRefresh) {
        setLoading(true);
      }

      // Utiliser .single() comme dans Projects.tsx
      const { data: entreprise, error: entrepriseError } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user?.id)
        .single();

      if (entrepriseError) {
        console.error('[FinancialManagement] Erreur entreprise:', entrepriseError);
        return;
      }

      if (!entreprise) {
        return;
      }

      setEntrepriseId(entreprise.id);

      const { data: chantiersData, error: chantiersError } = await supabase
        .from('chantiers')
        .select('*')
        .eq('entreprise_id', entreprise.id)
        .order('date_creation', { ascending: false });

      if (chantiersError) {
        console.error('[FinancialManagement] Erreur chantiers:', chantiersError);
        return;
      }

      if (chantiersData && chantiersData.length > 0) {
        // Charger les métriques pour chaque chantier (pour la sidebar)
        const chantiersAvecMetriques = await Promise.all(
          chantiersData.map(async (c) => {
            const { data: metricsData } = await supabase
              .from('chantier_metrics_realtime')
              .select('metrics')
              .eq('chantier_id', c.id)
              .maybeSingle();

            const metrics = metricsData?.metrics as any;
            return {
              ...c,
              etat_chantier: c.etat_chantier || 'en_cours',
              rentabilite: metrics?.marge_finale_pct || 0,
            };
          })
        );

        setChantiers(chantiersAvecMetriques);

        // Preserver le chantier selectionne lors des rechargements
        // Priorite : 1. Chantier actuel (si existe dans la liste) 2. URL/state/route 3. Sélecteur overlay
        // SECURITE: Verification que le chantierId appartient bien a l'entreprise de l'utilisateur
        const stateData = location.state as { chantierId?: string; openEditMode?: boolean } | null;
        const chantierIdFromUrl = searchParams.get('chantier');
        const chantierId = stateData?.chantierId || chantierIdFromUrl || chantierIdFromRoute;

        // Fonction interne pour acceder a l'etat actuel via callback
        setSelectedChantierId(prev => {
          // Priorité 1 : Si le chantier actuel existe toujours dans la liste, le conserver
          if (prev && chantiersAvecMetriques.some(c => c.id === prev)) {
            return prev;
          }
          // Priorité 2 : SECURITE — Verifier que le chantierId de l'URL/state/route appartient a l'utilisateur
          if (chantierId && chantiersAvecMetriques.some(c => c.id === chantierId)) {
            return chantierId;
          }
          // Priorité 2.5 : Mémoire de session (< 2 min) — éviter de resélectionner si retour rapide
          try {
            const recent = sessionStorage.getItem('financial_last_chantier');
            if (recent) {
              const { id: recentId, ts } = JSON.parse(recent);
              if (Date.now() - ts < 2 * 60 * 1000 && chantiersAvecMetriques.some(c => c.id === recentId)) {
                return recentId;
              }
            }
          } catch { /* sessionStorage indisponible ou données corrompues */ }
          // Priorité 3 : Pas de pré-sélection — afficher le sélecteur overlay
          // (uniquement au chargement initial, pas pendant les refresh silencieux)
          if (!silentRefresh) {
            setShowChantierSelector(true);
          }
          return prev; // Garder la valeur actuelle (vide au 1er chargement)
        });
      }
    } catch (error) {
      console.error("Erreur chargement chantiers:", error);
    } finally {
      // Ne pas mettre loading = false si c'était un refresh silencieux et qu'on n'avait pas mis loading = true
      if (!silentRefresh) {
        setLoading(false);
      }
    }
  };

  const loadChantierData = async () => {
    try {
      // Charger tous les devis du chantier
      const { data: devisData } = await supabase
        .from("devis")
        .select("*")
        .eq("chantier_id", selectedChantierId)
        .order("created_at", { ascending: false });

      setDevis(devisData || []);

      // Charger le retard existant (frais de type pénalité/retard)
      const { data: retardData } = await supabase
        .from("frais_chantier")
        .select("description")
        .eq("chantier_id", selectedChantierId)
        .or('type_frais.ilike.%Pénalité de retard%,type_frais.ilike.%Coût retard%')
        .order("date_frais", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (retardData?.description) {
        // Extraire le nombre de jours depuis la description (supporte les décimaux comme 1.5 ou 1,5)
        const match = retardData.description.match(/(\d+(?:[.,]\d+)?)\s*jour/);
        if (match) {
          // Remplacer la virgule par un point pour parseFloat
          setValidatedDelay(parseFloat(match[1].replace(',', '.')));
        }
      } else {
        setValidatedDelay(0);
      }

      // Charger les dates de la période de retard depuis les affectations
      const { data: retardAffectations } = await supabase
        .from("affectations_chantiers")
        .select("date_debut, date_fin")
        .eq("chantier_id", selectedChantierId)
        .like("notes", "Retard chantier%")
        .order("date_debut", { ascending: true });

      if (retardAffectations && retardAffectations.length > 0) {
        // Prendre la date de début la plus ancienne et la date de fin la plus récente
        const dateDebut = retardAffectations[0].date_debut;
        const dateFin = retardAffectations.reduce((max, a) =>
          a.date_fin > max ? a.date_fin : max, retardAffectations[0].date_fin);
        setDelayPeriod({ dateDebut, dateFin });
      } else {
        setDelayPeriod({ dateDebut: null, dateFin: null });
      }

      // Charger les factures
      const { data: facturesData } = await supabase
        .from("factures_fournisseurs")
        .select("*")
        .eq("chantier_id", selectedChantierId);
      setFactures(facturesData || []);

      // Charger les membres depuis affectations_chantiers
      const { data: affectationsData } = await supabase
        .from("affectations_chantiers")
        .select(`
          *,
          membres_equipe:membre_equipe_id (*)
        `)
        .eq("chantier_id", selectedChantierId);

      const membresAffectes = affectationsData?.map(a => ({
        ...a.membres_equipe,
        jours_travailles: a.jours_travailles || 0,
        affectation: {
          date_debut: a.date_debut,
          date_fin: a.date_fin,
          heures_par_jour: a.heures_par_jour,
          taux_horaire_specifique: a.taux_horaire_specifique
        }
      })) || [];
      setMembres(membresAffectes);

      // Charger les frais (exclure les coûts de retard main d'œuvre car déjà comptés via affectations)
      const { data: fraisData } = await supabase
        .from("frais_chantier")
        .select("*")
        .eq("chantier_id", selectedChantierId);
      // Filtrer côté client pour exclure les frais de type retard (main d'œuvre ET pénalités)
      // car ils sont déjà comptés et affichés séparément dans le simulateur/bilan de retard
      const fraisFiltres = (fraisData || []).filter(
        (f) => {
          const typeLower = f.type_frais?.toLowerCase() || '';
          return !typeLower.includes('coût retard') && !typeLower.includes('pénalité de retard');
        }
      );
      setFrais(fraisFiltres);

      // Charger la suspension active si le chantier existe
      // On doit d'abord récupérer l'état du chantier
      const chantierActuel = chantiers.find(c => c.id === selectedChantierId);
      if (chantierActuel?.etat_chantier === 'suspendu') {
        const { data: suspensionData } = await supabase
          .from('suspensions_chantiers')
          .select('*')
          .eq('chantier_id', selectedChantierId)
          .is('date_reprise', null) // Suspension active = pas de date de reprise
          .order('date_suspension', { ascending: false })
          .limit(1)
          .maybeSingle();

        setSuspensionInfo(suspensionData as SuspensionInfo | null);
      } else {
        setSuspensionInfo(null);
      }
    } catch (error) {
      console.error("Erreur chargement données chantier:", error);
    }
  };

  // Calculs automatiques (toujours en HT pour la rentabilité)
  const totalFactures = factures.reduce((sum, f) => sum + Number(f.montant_ht), 0);
  const totalFrais = frais.reduce((sum, f) => sum + Number(f.montant_ht ?? f.montant_total), 0);
  const coutsFixes = totalFactures + totalFrais;

  const devisActif = devis.find(d => d.actif);
  const calculations = useCalculations({
    membres,
    budget_devis: devisActif?.montant_ht || 0, // Utiliser HT pour les calculs de rentabilité
    couts_fixes: coutsFixes,
    date_debut: null,
  });

  const selectedChantier = chantiers.find(c => c.id === selectedChantierId);

  // === DONNÉES POUR LES BADGES D'ONGLETS ===
  const devisCount = devis.length;
  const devisStatusDot: 'emerald' | 'orange' | undefined = devis.some((d: any) => d.statut === 'accepte')
    ? 'emerald'
    : devisCount > 0 ? 'orange' : undefined;
  const membresCount = membres.length;
  const teamStatusDot: 'emerald' | undefined = membresCount > 0 ? 'emerald' : undefined;
  const facturesCount = factures.length;
  const fraisCount = frais.length;
  // Paiement: 0% = dot rouge, 1-99% = label "X%", 100% = checkmark vert
  const paiementPct = paiementTotals?.pourcentage_encaisse ?? null;
  const paiementCheckmark = paiementPct === 100;
  const paiementLabel = paiementPct != null && paiementPct > 0 && paiementPct < 100
    ? `${Math.round(paiementPct)}%`
    : undefined;
  const paiementDot: 'red' | undefined = paiementPct === 0 ? 'red' : undefined;
  // Rentabilité: label coloré selon statut
  const rentaLabel = metrics?.marge_finale_pct != null
    ? `${Math.round(metrics.marge_finale_pct)}%`
    : undefined;
  const rentaLabelBg = (() => {
    switch (metrics?.statut_rentabilite) {
      case 'VERT': return 'bg-emerald-500/20';
      case 'JAUNE': return 'bg-yellow-500/20';
      case 'ORANGE': return 'bg-orange-500/20';
      case 'ROUGE': return 'bg-red-500/20';
      default: return undefined;
    }
  })();
  const rentaLabelText = (() => {
    switch (metrics?.statut_rentabilite) {
      case 'VERT': return 'text-emerald-400';
      case 'JAUNE': return 'text-yellow-400';
      case 'ORANGE': return 'text-orange-400';
      case 'ROUGE': return 'text-red-400';
      default: return undefined;
    }
  })();
  // Retard: label "+Xj" orange si retard validé
  const retardLabel = validatedDelay > 0 ? `+${validatedDelay}j` : undefined;

  // Hook pour obtenir la date de fin effective (avec retards et suspensions)
  const { dateEffective, totalJoursRetard, totalJoursSuspension, isLoading: isLoadingDateFin } = useChantierDateFin({
    chantierId: selectedChantierId,
    dateFinEstimee: selectedChantier?.date_fin_estimee ?? null,
    dateFinInitiale: (selectedChantier as any)?.date_fin_initiale ?? null,
    enabled: !!selectedChantierId && !!selectedChantier?.date_fin_estimee
  });

  // Date de fin à utiliser partout (effective si disponible, sinon estimée)
  const dateFinEffective = dateEffective
    ? format(dateEffective, 'yyyy-MM-dd')
    : selectedChantier?.date_fin_estimee;

  // Fonction pour retourner à la clôture du chantier
  const handleReturnToClosure = () => {
    if (closureChantierId) {
      clearClosure();
      navigate(`/projects/${closureChantierId}?action=closure`);
    }
  };

  // Fonction pour annuler le retour à la clôture
  const handleDismissClosure = () => {
    clearClosure();
  };

  // Calcul des jours ouvrés effectués et prévus (basé sur les affectations réelles)
  // Utilise dateFinEffective qui inclut retards et suspensions
  const joursOuvresInfo = useMemo(() => {
    // Calculer les jours depuis les affectations réelles si disponibles
    if (membres && membres.length > 0) {
      // Somme des jours travaillés de toutes les affectations
      const joursEffectues = membres.reduce((sum, m) => sum + (m.jours_travailles || 0), 0);

      // Jours prévus = basé sur dateFinEffective (inclut retards/suspensions)
      const joursPrevus = dateFinEffective && selectedChantier?.date_debut_prevue
        ? calculerJoursOuvres(selectedChantier.date_debut_prevue, dateFinEffective)
        : joursEffectues;

      return { joursEffectues, joursPrevus };
    }

    // Fallback sur les dates du chantier si pas d'affectations
    if (!selectedChantier?.date_debut_prevue) return null;

    const today = new Date();
    const dateDebut = selectedChantier.date_debut_prevue;

    // Utilise dateFinEffective (inclut retards/suspensions)
    const dateFin = dateFinEffective;

    // Jours effectués (du début jusqu'à aujourd'hui, max jusqu'à la date de fin)
    const finEffectiveCalc = dateFin && new Date(dateFin) < today ? dateFin : today;
    const joursEffectues = calculerJoursOuvres(dateDebut, finEffectiveCalc);

    // Jours prévus (du début jusqu'à la fin effective)
    const joursPrevus = dateFin ? calculerJoursOuvres(dateDebut, dateFin) : null;

    return { joursEffectues, joursPrevus };
  }, [membres, selectedChantier?.date_debut_prevue, dateFinEffective]);

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-up">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
            <Building className="h-9 w-9 text-primary" />
            Gestion financière
          </h1>
        </div>
        <Card className="card-premium">
          <CardContent className="pt-16 pb-16 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
            <p className="text-muted-foreground text-lg font-medium">Chargement des données...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (chantiers.length === 0) {
    return (
      <div className="space-y-8 animate-fade-up">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
            <Building className="h-9 w-9 text-primary" />
            Gestion financière
          </h1>
        </div>
        <Card className="card-premium">
          <CardContent className="pt-16 pb-16 text-center">
            <Building className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-xl font-semibold text-muted-foreground mb-2">
              Aucun chantier disponible
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Créez votre premier chantier pour commencer le suivi financier
            </p>
            <Button onClick={() => navigate("/projects")}>
              Aller aux chantiers
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Overlay de sélection de chantier (arrivée depuis le menu sans pré-sélection)
  if (showChantierSelector) {
    return (
      <ChantierSelectorOverlay
        chantiers={chantiers}
        onSelectChantier={(id) => {
          setSelectedChantierId(id);
          setShowChantierSelector(false);
          // Mémoriser la sélection pour 2 min (éviter resélection si retour rapide)
          try { sessionStorage.setItem('financial_last_chantier', JSON.stringify({ id, ts: Date.now() })); } catch {}
        }}
      />
    );
  }

  return (
    <>
    <div className="animate-fade-up space-y-8">
        {/* Bannière de retour à la clôture */}
        {fromClosureModal && closureChantierId === selectedChantierId && (
          <Alert className="bg-green-500/10 border-green-500/30 animate-closure-banner">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm">
                <strong>Clôture en cours</strong> — Une fois le retard enregistré, vous pouvez continuer la clôture du chantier.
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDismissClosure}
                  className="h-8"
                >
                  <X className="h-4 w-4 mr-1" />
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={handleReturnToClosure}
                  className="h-8 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Retour à la clôture
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="space-y-6">
          <Button onClick={() => navigate("/projects")} variant="ghost" className="hover-lift group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Retour aux chantiers
          </Button>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
                <Building className="h-9 w-9 text-primary" />
                Gestion financière
              </h1>
              {selectedChantier && (
                <div className="mt-3 space-y-2">
                  <p className="text-xl font-semibold text-foreground flex items-center gap-2">
                    {selectedChantier.pictogramme && selectedChantier.couleur ? (
                      <ChantierIcon
                        pictogramme={selectedChantier.pictogramme}
                        couleur={selectedChantier.couleur}
                        size="md"
                        showBackground={true}
                      />
                    ) : (
                      <Building className="h-6 w-6 text-muted-foreground" />
                    )}
                    {selectedChantier.nom_chantier}
                  </p>
                  <p className="text-gray-300">
                    Client : {selectedChantier.client}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300">
                    {/* Dates - utilise dateFinEffective (inclut retards/suspensions) */}
                    {(selectedChantier.date_debut_prevue || dateFinEffective) && (
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {selectedChantier.date_debut_prevue
                              ? new Date(selectedChantier.date_debut_prevue).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '?'
                            }
                            {' → '}
                            {dateFinEffective
                              ? new Date(dateFinEffective).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '?'
                            }
                          </span>
                          {/* Jours ouvrés effectués / prévus */}
                          {(metrics || joursOuvresInfo) && (
                            <>
                              <span className="text-muted-foreground/50 mx-1">•</span>
                              <Clock className="h-4 w-4" />
                              <span>
                                {metrics?.jours_ecoules ?? joursOuvresInfo?.joursEffectues ?? 0} jour{(metrics?.jours_ecoules ?? joursOuvresInfo?.joursEffectues ?? 0) > 1 ? 's' : ''}
                                {(metrics?.duree_estimee_jours || joursOuvresInfo?.joursPrevus) ? ` / ${metrics?.duree_estimee_jours ?? joursOuvresInfo?.joursPrevus} prévus` : ' effectués'}
                                {validatedDelay > 0 && (
                                  <span className="text-warning font-medium"> + {validatedDelay} jour{validatedDelay > 1 ? 's' : ''} de retard</span>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                        {/* Période de retard */}
                        {delayPeriod.dateDebut && delayPeriod.dateFin && (
                          <div className="flex items-center gap-1.5 text-warning font-medium ml-5">
                            <span>
                              Retard : {new Date(delayPeriod.dateDebut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                              {/* Afficher la flèche et date fin seulement si différente de date début */}
                              {delayPeriod.dateDebut !== delayPeriod.dateFin && (
                                <>
                                  {' → '}
                                  {new Date(delayPeriod.dateFin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                </>
                              )}
                            </span>
                          </div>
                        )}
                        {/* Période de suspension */}
                        {suspensionInfo && (
                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <div className="flex items-center gap-1.5 text-amber-500 font-medium ml-5 cursor-help">
                                <Pause className="h-3.5 w-3.5" />
                                <span>
                                  Suspendu : {new Date(suspensionInfo.date_suspension).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                  {' → '}
                                  {suspensionInfo.duree_estimee === 'personnalisee' && suspensionInfo.date_reprise_estimee
                                    ? new Date(suspensionInfo.date_reprise_estimee).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                                    : formatDureeEstimee(suspensionInfo.duree_estimee)
                                  }
                                  {metrics?.jours_ecoules !== undefined && (
                                    <span className="text-muted-foreground font-normal">
                                      {' '}({metrics.jours_ecoules}j travaillés)
                                    </span>
                                  )}
                                </span>
                              </div>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-64">
                              <div className="space-y-2">
                                <p className="font-semibold text-sm">Raison de la suspension</p>
                                <p className="text-sm text-muted-foreground">
                                  {formatRaison(suspensionInfo.raison)}
                                  {suspensionInfo.raison === 'autre' && suspensionInfo.raison_detail && (
                                    <> : {suspensionInfo.raison_detail}</>
                                  )}
                                </p>
                                {suspensionInfo.date_reprise_estimee && (
                                  <p className="text-xs text-muted-foreground">
                                    Reprise estimée : {new Date(suspensionInfo.date_reprise_estimee).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                  </p>
                                )}
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs avec 7 sous-onglets */}
        <Card className="card-premium shadow-xl">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="flex w-full h-auto bg-muted/30 p-1.5 gap-1 border-b border-white/10 overflow-x-auto [&>*]:grow [&>*]:shrink [&>*]:min-w-0" data-miro-target="financial-tabs-bar">
              <TabsTrigger
                value="profitability"
                className={cn(TAB_TRIGGER_CLASSES, "py-3")}
              >
                <span className="hidden sm:inline">📊 Rentabilité</span>
                <span className="sm:hidden">📊</span>
                <TabBadge label={rentaLabel} labelBgClass={rentaLabelBg} labelTextClass={rentaLabelText} hideCountOnMobile />
              </TabsTrigger>
              <TabsTrigger
                value="quote"
                className={cn(TAB_TRIGGER_CLASSES, "py-3")}
              >
                <span className="hidden sm:inline">📄 Devis</span>
                <span className="sm:hidden">📄</span>
                <TabBadge count={devisCount} dotColor={devisStatusDot} hideCountOnMobile />
              </TabsTrigger>
              <TabsTrigger
                value="team"
                className={cn(TAB_TRIGGER_CLASSES, "py-3")}
              >
                <span className="hidden sm:inline">👥 Équipe</span>
                <span className="sm:hidden">👥</span>
                <TabBadge count={membresCount} dotColor={teamStatusDot} hideCountOnMobile />
              </TabsTrigger>
              <TabsTrigger
                value="invoices"
                className={cn(TAB_TRIGGER_CLASSES, "py-3")}
              >
                <span className="hidden sm:inline">💳 Factures</span>
                <span className="sm:hidden">💳</span>
                <TabBadge count={facturesCount} hideCountOnMobile />
              </TabsTrigger>
              <TabsTrigger
                value="expenses"
                className={cn(TAB_TRIGGER_CLASSES, "py-3")}
              >
                <span className="hidden sm:inline">📦 Coûts annexes</span>
                <span className="sm:hidden">📦</span>
                <TabBadge count={fraisCount} hideCountOnMobile />
              </TabsTrigger>
              <TabsTrigger
                value="paiement"
                className={cn(TAB_TRIGGER_CLASSES, "py-3")}
              >
                <span className="hidden sm:inline">💰 Paiement</span>
                <span className="sm:hidden">💰</span>
                <TabBadge checkmark={paiementCheckmark} label={paiementLabel} labelBgClass="bg-orange-500/20" labelTextClass="text-orange-400" dotColor={paiementDot} />
              </TabsTrigger>
              <TabsTrigger
                value="penalty-simulator"
                className={cn(TAB_TRIGGER_CLASSES, "py-3")}
                data-miro-target="financial-tab-retard"
              >
                <span className="hidden sm:inline">⏱️ Retard/Pénalité</span>
                <span className="sm:hidden">⏱️</span>
                <TabBadge label={retardLabel} labelBgClass="bg-orange-500/20" labelTextClass="text-orange-400" hideCountOnMobile />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profitability" className="mt-6 p-6">
              <ProfitabilityView
                metrics={metrics}
                loading={metricsLoading}
                chantierId={selectedChantierId}
                dateDebutChantier={selectedChantier?.date_debut_prevue}
                dateFinPrevue={dateFinEffective}
                joursRetard={validatedDelay > 0 ? validatedDelay : undefined}
                criticalDelayDays={criticalDelayDays}
              />
            </TabsContent>

            <TabsContent value="quote" className="mt-6 p-6">
              <QuoteManager
                chantierId={selectedChantierId}
                devis={devis}
                onUpdate={loadChantierData}
              />
            </TabsContent>

            <TabsContent value="team" className="mt-6 p-6">
              <AffectationsList
                chantierId={selectedChantierId}
                entrepriseId={entrepriseId}
              />
            </TabsContent>

            <TabsContent value="invoices" className="mt-6 p-6">
              <InvoiceManager
                chantierId={selectedChantierId}
                factures={factures}
                onUpdate={loadChantierData}
              />
            </TabsContent>

            <TabsContent value="expenses" className="mt-6 p-6">
              <ExpensesManager
                chantierId={selectedChantierId}
                frais={frais}
                onUpdate={loadChantierData}
              />
            </TabsContent>

            <TabsContent value="penalty-simulator" className="mt-6 p-6 data-[state=inactive]:hidden" forceMount>
              <DelayPenaltySimulator
                chantierId={selectedChantierId}
                metrics={metrics}
                entrepriseId={entrepriseId}
                nomChantier={selectedChantier?.nom_chantier}
                client={selectedChantier?.client}
                openEditMode={(location.state as { openEditMode?: boolean })?.openEditMode}
                onSavePenalty={async (delayDays) => {
                  setValidatedDelay(delayDays);
                  // Recalculer les métriques pour prendre en compte le retard
                  await refreshMetrics();
                  loadChantierData();
                  // Recharger aussi la liste des chantiers pour mettre à jour la marge dans la sidebar (silencieux)
                  loadChantiers(true);
                }}
                onCriticalDelayDaysChange={setCriticalDelayDays}
              />
            </TabsContent>

            <TabsContent value="paiement" className="mt-6 p-6">
              <PaiementClient
                chantierId={selectedChantierId}
                montantDevisHT={devisActif?.montant_ht || 0}
                montantDevisTTC={devisActif?.montant_ttc}
                onUpdate={loadChantierData}
              />
            </TabsContent>
          </Tabs>
        </Card>
    </div>

    {/* Sidebar de sélection des chantiers - Panel fixe bord droit */}
    <div data-miro-target="financial-chantiers-panel">
      <ChantiersSidebarPanel
        selectedChantierId={selectedChantierId}
        onSelectChantier={(id: string) => {
          setSelectedChantierId(id);
          // Mémoriser la sélection pour 2 min
          try { sessionStorage.setItem('financial_last_chantier', JSON.stringify({ id, ts: Date.now() })); } catch {}
        }}
        chantiers={chantiers}
        loading={loading}
      />
    </div>

    {/* Annotations Miro Gestion Financière */}
    <MiroAnnotations
      pageId="financial"
      annotations={[
        {
          id: "financial-tabs",
          targetSelector: "[data-miro-target='financial-tabs-bar']",
          text: "Suivez le cycle financier : Devis → Équipe → Factures → Coûts → Paiement. L'onglet Rentabilité fait la synthèse",
          position: "bottom",
          offsetX: 0,
          offsetY: 20,
        },
        {
          id: "financial-sidebar",
          targetSelector: "[data-miro-target='financial-chantiers-panel']",
          text: "Sélectionnez un chantier pour voir ses données financières détaillées",
          position: "left",
          offsetX: -30,
          offsetY: -250,
        },
        {
          id: "financial-retard",
          targetSelector: "[data-miro-target='financial-tab-retard']",
          text: "Simulez l'impact financier d'un retard : entrez un nombre de jours pour voir l'effet sur votre marge",
          position: "top",
          offsetX: 0,
          offsetY: -20,
        },
      ]}
    />
    </>
  );
};

export default FinancialManagement;
